import { useState, useCallback, useRef } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import type { ReactNode, TouchEvent as ReactTouchEvent } from 'react';
import { cn } from '@ownyou/ui-design-system';
import { prefersReducedMotion } from '@/utils/animations';

export interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void>;
  className?: string;
  threshold?: number;
  minVisibleTime?: number;
}

const MIN_VISIBLE_TIME = 500;

export function PullToRefresh({
  children,
  onRefresh,
  className,
  threshold = 80,
  minVisibleTime = MIN_VISIBLE_TIME,
}: PullToRefreshProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const pullDistance = useMotionValue(0);
  const opacity = useTransform(pullDistance, [0, threshold], [0, 1]);
  const scale = useTransform(pullDistance, [0, threshold], [0.5, 1]);
  const rotate = useTransform(pullDistance, [0, threshold * 2], [0, 360]);
  const reduced = prefersReducedMotion();

  const handleTouchStart = useCallback((e: ReactTouchEvent) => {
    if (isRefreshing) return;
    startY.current = e.touches[0].clientY;
  }, [isRefreshing]);

  const handleTouchMove = useCallback((e: ReactTouchEvent) => {
    if (isRefreshing) return;
    const currentY = e.touches[0].clientY;
    const diff = Math.max(0, currentY - startY.current);
    // Apply resistance — diminishing returns past threshold
    const dampened = diff > threshold ? threshold + (diff - threshold) * 0.3 : diff;
    pullDistance.set(dampened);
  }, [isRefreshing, pullDistance, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (isRefreshing) return;
    const distance = pullDistance.get();

    if (distance >= threshold) {
      setIsRefreshing(true);
      const startTime = Date.now();

      try {
        await onRefresh();
      } finally {
        const elapsed = Date.now() - startTime;
        const remaining = minVisibleTime - elapsed;
        if (remaining > 0) {
          await new Promise((r) => setTimeout(r, remaining));
        }
        setIsRefreshing(false);
        pullDistance.set(0);
      }
    } else {
      pullDistance.set(0);
    }
  }, [isRefreshing, pullDistance, threshold, onRefresh, minVisibleTime]);

  return (
    <div
      data-testid="pull-to-refresh"
      className={cn('relative overflow-hidden', className)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <motion.div
        data-testid="pull-indicator"
        className="absolute top-0 left-0 right-0 flex items-center justify-center pointer-events-none"
        style={{ opacity, height: threshold }}
      >
        <motion.div
          className={cn(
            'w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center',
            'text-white font-bold text-lg shadow-md',
          )}
          style={reduced ? {} : { scale, rotate: isRefreshing ? undefined : rotate }}
          animate={isRefreshing && !reduced ? { rotate: 360 } : undefined}
          transition={isRefreshing ? { duration: 1, repeat: Infinity, ease: 'linear' } : undefined}
        >
          O
        </motion.div>
      </motion.div>

      {/* Content */}
      <motion.div style={reduced ? {} : { y: pullDistance }}>
        {children}
      </motion.div>
    </div>
  );
}
